import React, { memo, useEffect, useRef, useState } from "react";

import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { GripVertical } from "lucide-react";
import { useTranslation } from "react-i18next";

import SectionHeader from "@/src/components/form-fields/SectionHeader";
import { SwitchFieldBase } from "@/src/components/form-fields/SwitchField";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/src/components/ui/accordion";
import { Badge } from "@/src/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";
import { cn } from "@/src/lib/utils";

import AdvancedSearch from "./AdvancedSearch";
import ImageFormatSelector from "./ImageFormatSelector";
import PlaceholderInputSearch from "./PlaceholderInputSearch";
import SmartlistDataSource from "../../../components/utils/SmartlistDataSource";
import { COMPONENT_TYPES } from "../../../constants/componentType";
import {
  ADDITIONNAL_IMAGE_FORMATS,
  DEFAULT_IMAGE_FORMATS,
  IMAGE_FORMATS,
} from "../../../constants/ratio";
import {
  SMARTLIST_TAB,
  USER_RECOMMENDATION_TAB,
} from "../../../types/platform";
import {
  isSearchResultComponent,
  reorderSearchResultComponents,
  sortSearchResultComponents,
} from "../../../utils/searchPageResults";

import type {
  DraggableProvidedDragHandleProps,
  DropResult,
} from "@hello-pangea/dnd";
import type { JSX } from "react";

import type {
  ComponentWithUnknownDataSourceParams,
  SearchComponent,
  SearchPagePlatformComponent,
  SearchPlaceholderComponent,
  SmartlistDataSourceComponent,
  UserRecommendationComponent,
} from "../../../api/ProjectAPI.types";
import type { ImageFormat } from "../../../constants/ratio";
import type { SmartlistDataSourceTab } from "../../../types/platform";
import type { SearchResultModuleName } from "../../../utils/searchPageResults";

const { SEARCH_PLACEHOLDER } = COMPONENT_TYPES;

const SEARCH_RESULT_TITLE_KEY_MAP: Record<SearchResultModuleName, string> = {
  search_media: "contentsResults",
  search_fast: "fastsResults",
  search_smartlist: "smartlistsResults",
  search_casting: "castingsResults",
};

interface SearchSectionProps {
  title: string;
  description: string;
  enabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  children: React.ReactNode;
}

const DEFAULT_DISPLAY_TAB_LIST: SmartlistDataSourceTab[] = [
  SMARTLIST_TAB,
  USER_RECOMMENDATION_TAB,
];

function isDefaultDisplayComponent(
  component: SearchPagePlatformComponent,
): boolean {
  return component.rank === 1 && !isSearchResultComponent(component);
}

function SearchSection({
  title,
  description,
  enabled,
  onEnabledChange,
  children,
}: SearchSectionProps): JSX.Element {
  const isDimmed = onEnabledChange !== undefined && !enabled;

  return (
    <section className="flex flex-col gap-4 border-t px-4 py-6 sm:px-8">
      <SectionHeader
        checked={enabled}
        description={description}
        onCheckedChange={onEnabledChange}
        title={title}
      />
      <div
        className={cn(
          "flex flex-col gap-4",
          isDimmed && "pointer-events-none opacity-45",
        )}
      >
        {children}
      </div>
    </section>
  );
}

interface SearchPageEditorProps {
  components: SearchPagePlatformComponent[];
  onChangeComponents: (components: SearchPagePlatformComponent[]) => void;
}

function SearchPageEditor({
  components,
  onChangeComponents,
}: SearchPageEditorProps): JSX.Element {
  const { t } = useTranslation("editor-platform");
  const updateComponent = (
    uuid: string,
    updater: (
      component: SearchPagePlatformComponent,
    ) => SearchPagePlatformComponent,
  ): void => {
    onChangeComponents(
      components.map((component) =>
        component.uuid === uuid ? updater(component) : component,
      ),
    );
  };

  const onChangeOptions = <T extends SearchPagePlatformComponent>(
    component: T,
    option: keyof T,
    value: T[keyof T],
  ): void =>
    updateComponent(component.uuid, (current) => ({
      ...current,
      [option]: value,
    }));

  const onChangeParamsOption = (
    component: SearchPagePlatformComponent,
    option: string,
    value: string | boolean,
  ): void =>
    updateComponent(
      component.uuid,
      (current) =>
        ({
          ...current,
          params: { ...current.params, [option]: value },
        }) as SearchPagePlatformComponent,
    );

  const handleComponentUpdate = (
    item: UserRecommendationComponent | SmartlistDataSourceComponent,
  ): void => {
    const updatedComponent = components.map((comp) => {
      if (isDefaultDisplayComponent(comp)) {
        return item;
      }
      return comp;
    });

    onChangeComponents(updatedComponent as SearchPagePlatformComponent[]);
  };

  const renderAdvancedSearch = (item: SearchComponent): JSX.Element => {
    const { enabled, params } = item;
    const { max_results: maxResults, ...filters } = params;
    return (
      <AdvancedSearch
        enabled={enabled}
        filters={filters}
        maxResults={maxResults}
        onChangeFilters={(updatedFilters): void => {
          onChangeOptions(item, "params", {
            ...updatedFilters,
            max_results: maxResults,
          });
        }}
        onChangeMaxResults={(maxRes: number): void =>
          onChangeOptions(item, "params", { max_results: maxRes, ...filters })
        }
      />
    );
  };

  const renderSearchResultItem = (
    component: SearchPagePlatformComponent,
    index: number,
    dragHandleProps: DraggableProvidedDragHandleProps | null,
  ): JSX.Element | null => {
    if (component.type === "search_placeholder" || component.type === "search")
      return null;
    if (!isSearchResultComponent(component)) return null;

    const moduleName = component.static_component.smartlist_module_name;
    const { ratio } = component.params;
    const displayItemTitle = component.params.display_item_title ?? false;
    const displayDialog = component.params.display_dialog ?? false;
    const isCasting = moduleName === "search_casting";
    const imageFormatList: ImageFormat[] = [
      ...DEFAULT_IMAGE_FORMATS,
      ...ADDITIONNAL_IMAGE_FORMATS,
      ...(isCasting ? [IMAGE_FORMATS.FORMAT_1_1_ROUND] : []),
    ];

    return (
      <React.Fragment>
        <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-1 pl-1">
          <div
            aria-label={t("dragToReorder")}
            className="text-muted-foreground focus-visible:ring-ring/50 flex h-11 w-9 shrink-0 cursor-grab items-center justify-center rounded-md outline-none focus-visible:ring-[3px]"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...dragHandleProps}
          >
            <GripVertical aria-hidden="true" className="size-4" />
          </div>
          <AccordionTrigger className="items-center py-3 pr-4 hover:no-underline [&>svg]:translate-y-0">
            <span className="flex min-w-0 flex-1 items-center gap-3.5">
              <span
                aria-hidden="true"
                className="bg-muted flex size-6.5 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
              >
                {index + 1}
              </span>
              <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
                <span className="min-w-0 truncate text-base font-semibold">
                  {t(SEARCH_RESULT_TITLE_KEY_MAP[moduleName])}
                </span>
                <span className="flex gap-1.5">
                  <Badge variant="secondary">
                    {t("formatSummary", { ratio: t(ratio) })}
                  </Badge>
                  <Badge variant="secondary">
                    {t(displayItemTitle ? "titlesShown" : "titlesHidden")}
                  </Badge>
                </span>
              </span>
            </span>
          </AccordionTrigger>
        </div>
        <AccordionContent className="flex flex-col gap-5 border-t py-4 pr-4 pl-4 sm:pr-6 sm:pl-21">
          <ImageFormatSelector
            className="pb-0"
            imageFormatList={imageFormatList}
            selected={ratio}
            title={t("pictureRatioContent")}
            onChangeRatio={(selectedRatio: ImageFormat): void =>
              onChangeParamsOption(component, "ratio", selectedRatio)
            }
          />
          <div className="flex flex-col gap-3">
            <SwitchFieldBase
              checked={displayItemTitle}
              label={t(isCasting ? "showCastingsName" : "showSearchItemsTitle")}
              variant="inline"
              onCheckedChange={(checked: boolean): void =>
                onChangeParamsOption(component, "display_item_title", checked)
              }
            />
            {moduleName === "search_fast" ? (
              <SwitchFieldBase
                checked={displayDialog}
                label={t("showDialog")}
                variant="inline"
                onCheckedChange={(checked: boolean): void =>
                  onChangeParamsOption(component, "display_dialog", checked)
                }
              />
            ) : null}
          </div>
        </AccordionContent>
      </React.Fragment>
    );
  };

  const placeholderComponent = components.find(
    (component): component is SearchPlaceholderComponent =>
      component.type === SEARCH_PLACEHOLDER,
  );
  const searchComponent = components.find(
    (component): component is SearchComponent => component.type === "search",
  );
  const defaultDisplayComponent = components.find(isDefaultDisplayComponent) as
    | UserRecommendationComponent
    | SmartlistDataSourceComponent
    | undefined;

  const searchResultComponentList = sortSearchResultComponents(
    components.filter(isSearchResultComponent),
  );

  const [openValue, setOpenValue] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const onSearchResultDragEnd = (result: DropResult): void => {
    setIsDragging(false);
    if (!result.destination) return;

    const reorderedList = reorderSearchResultComponents(
      searchResultComponentList,
      result.source.index,
      result.destination.index,
    );
    const rankByUuid = new Map(
      reorderedList.map((component) => [component.uuid, component.rank]),
    );

    onChangeComponents(
      components.map((component): SearchPagePlatformComponent => {
        const rank = rankByUuid.get(component.uuid);
        return rank === undefined ? component : { ...component, rank };
      }),
    );
  };

  const hasInitializedOpenValue = useRef(false);
  const firstResultUuid = searchResultComponentList[0]?.uuid;

  useEffect(() => {
    if (!hasInitializedOpenValue.current && firstResultUuid) {
      hasInitializedOpenValue.current = true;
      setOpenValue(firstResultUuid);
    }
  }, [firstResultUuid]);

  return (
    <div className="flex flex-col gap-5">
      <Card className="gap-0 py-0">
        <CardHeader className="px-4 py-6 sm:px-8">
          <CardTitle className="text-lg">{t("searchConfiguration")}</CardTitle>
          <CardDescription className="text-sm">
            {t("searchConfigurationDescription")}
          </CardDescription>
        </CardHeader>
        {placeholderComponent ? (
          <SearchSection
            description={t("placeholderSearchDescription")}
            enabled={placeholderComponent.enabled}
            title={t("placeholder_search")}
            onEnabledChange={(enabled: boolean): void =>
              onChangeOptions(placeholderComponent, "enabled", enabled)
            }
          >
            <PlaceholderInputSearch
              enabled={placeholderComponent.enabled}
              placeholdersList={placeholderComponent.params.placeholders}
              onAddPlaceholders={(updatedPlaceholders: string[]): void => {
                onChangeOptions(placeholderComponent, "params", {
                  placeholders: updatedPlaceholders,
                });
              }}
            />
          </SearchSection>
        ) : null}
        {searchComponent ? (
          <SearchSection
            description={t("advancedSearchDescription")}
            enabled={searchComponent.enabled}
            title={t("advancedSearch")}
            onEnabledChange={(enabled: boolean): void =>
              onChangeOptions(searchComponent, "enabled", enabled)
            }
          >
            {renderAdvancedSearch(searchComponent)}
          </SearchSection>
        ) : null}
        {defaultDisplayComponent ? (
          <SearchSection
            description={t("defaultDisplayDescription")}
            title={t("defaultDisplay")}
          >
            <SmartlistDataSource
              item={defaultDisplayComponent}
              tabs={DEFAULT_DISPLAY_TAB_LIST}
              onComponentUpdate={
                handleComponentUpdate as (
                  item: ComponentWithUnknownDataSourceParams,
                ) => void
              }
            />
          </SearchSection>
        ) : null}
      </Card>
      <Card className="gap-0 py-0">
        <CardHeader className="px-4 pt-6 pb-4 sm:px-8">
          <CardTitle className="text-lg">{t("searchResults")}</CardTitle>
          <CardDescription className="text-sm">
            {t("searchResultsDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-6 sm:px-8">
          <Accordion
            collapsible
            className="[&[data-dragging]_[data-slot=accordion-content]]:animate-none"
            data-dragging={isDragging ? "" : undefined}
            type="single"
            value={isDragging ? "" : openValue}
            onValueChange={(value: string): void => {
              if (!isDragging) setOpenValue(value);
            }}
          >
            <DragDropContext
              onBeforeCapture={(): void => setIsDragging(true)}
              onDragEnd={onSearchResultDragEnd}
            >
              <Droppable droppableId="search-results">
                {(droppableProvided) => (
                  <div
                    ref={droppableProvided.innerRef}
                    className="flex flex-col"
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...droppableProvided.droppableProps}
                  >
                    {searchResultComponentList.map((component, index) => (
                      <Draggable
                        key={component.uuid}
                        draggableId={component.uuid}
                        index={index}
                      >
                        {(draggableProvided) => (
                          <div
                            ref={draggableProvided.innerRef}
                            className="mb-2"
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...draggableProvided.draggableProps}
                          >
                            <AccordionItem
                              className="bg-card data-[state=open]:border-primary/50 rounded-lg border last:border-b"
                              value={component.uuid}
                            >
                              {renderSearchResultItem(
                                component,
                                index,
                                draggableProvided.dragHandleProps,
                              )}
                            </AccordionItem>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}

export default memo(SearchPageEditor);
